import { Box } from '@primer/react';
import { FinalImpactScoreType, NetScoreFormat } from '@upright/common/model/output/scores/types';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import {
  formatNetScore,
  ImpactScoreFormatters,
} from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-formatter';
import { CSSProperties } from 'react';
import styled from 'styled-components';
import { Bounds, Side } from '../../../impact-lens-profile/common/layout/types';
import { useCarouselLayout } from '../../../impact-lens-profile/context';
import { Root, Title } from '../../../impact-lens-profile/frame/dimension-titles/dimension-title-with-icon';
import { useNetScoreAdjustedWithValueSet } from '../aggregate-result/common/use-net-score';
import { useNetImpactLensContext } from '../common/use-net-impact-lens-context';
import { E2eTestElementId } from '@upright/test-ids/dom-elements';

export function TotalSumsArea() {
  const negativeSumsArea = useCarouselLayout().totalSumsDataArea(Side.LEFT);
  const positiveSumsArea = useCarouselLayout().totalSumsDataArea(Side.RIGHT);
  const titleArea = useCarouselLayout().totalSumsTitleArea();

  const netScore = useNetScoreAdjustedWithValueSet(NetScoreFormat.NET_SUM);
  const context = useNetImpactLensContext();
  const options = context?.options;
  const { formatters, scoreUnit } = options ?? {};

  const formattedTotalSums = formatTotalSums({
    pos: netScore?.valenceSums.pos,
    neg: netScore?.valenceSums.neg,
    formatters,
    scoreUnit,
  });

  if (!context?.state.showTotalSums) return <></>;

  return (
    <Box data-testid={E2eTestElementId.NI_CHART_TOTAL_SUMS}>
      <LeftHandSumRoot style={getWrapperStyle(negativeSumsArea)}>{formattedTotalSums.neg}</LeftHandSumRoot>
      <Root style={getWrapperStyle(titleArea)}>
        <Title>Total</Title>
      </Root>
      <RightHandSumRoot style={getWrapperStyle(positiveSumsArea)}>{formattedTotalSums.pos}</RightHandSumRoot>
    </Box>
  );
}

function getWrapperStyle(area: Bounds) {
  const common: CSSProperties = {
    top: area.y1,
    left: area.x1,
    width: area.width,
    height: area.height,
    position: 'absolute',
  };

  return common;
}

export const LeftHandSumRoot = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: end;
  text-align: center;
  border-radius: 2px;
  color: ${BouColor.PLATFORM_TEXT_GRAY};
  padding-right: 4px;
`;

const RightHandSumRoot = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: start;
  text-align: center;
  border-radius: 2px;
  color: ${BouColor.PLATFORM_TEXT_GRAY};
  padding-left: 4px;
`;

interface FormatterProps {
  pos: number | undefined;
  neg: number | undefined;
  formatters: ImpactScoreFormatters | undefined;
  scoreUnit: FinalImpactScoreType | undefined;
}

const formatTotalSums = (props: FormatterProps) => {
  const { pos, neg, formatters, scoreUnit } = props;
  return {
    pos: pos && formatters && scoreUnit ? formatNetScore(pos, NetScoreFormat.NET_SUM, formatters, scoreUnit) : null,
    neg: neg && formatters && scoreUnit ? formatNetScore(-neg, NetScoreFormat.NET_SUM, formatters, scoreUnit) : null,
  };
};
